// ─── Guided Setup: who is allowed where ───────────────────────────────────────
//
// Per camera, the enrolled people who belong there: detector_config.unknown
// .allowed_names. With a list set, anyone NOT on it raises an unauthorised-access
// alert even though their face is enrolled — which is the whole point. A plant
// wants the stores clerk recognised at the stores door and reported at the
// substation.
//
// An EMPTY list is not "nobody allowed", it is "no restriction" — every enrolled
// person is let through. That inversion is the one thing somebody can get badly
// wrong here, so the panel says which state each camera is in rather than showing
// an empty box and leaving it to be guessed.
//
// Only cameras that recognise faces can enforce this. A person-only camera has no
// idea who it is looking at, so a list there would do nothing — those are listed
// separately instead of silently accepting a setting that cannot work.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../utils/streamApi';
import { Panel, useModuleSaver } from './SetupBits';

const FACE_KEYS = ['unknown', 'known', 'banned'];

export default function AccessPanel({ cameras, onChanged }) {
  const [cams,  setCams]  = useState(null);
  const [known, setKnown] = useState([]);
  const [open,  setOpen]  = useState(null);     // camera being edited
  const [draft, setDraft] = useState([]);
  const { busy, err, note, setErr, run } = useModuleSaver(apiFetch, onChanged);

  const load = useCallback(() => apiFetch('/ui/cameras')
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d) setCams(d.cameras || []); })
    .catch(() => setErr('Could not read the cameras.')), [setErr]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch('/faces/known')
      .then(r => (r.ok ? r.json() : null))
      .then(d => setKnown((d?.persons || []).map(p => p.name)))
      .catch(() => {});
  }, []);

  // Only the cameras this step was given (the armed ones), in their order.
  const rows = (cams || [])
    .filter(c => cameras.some(x => x.idx === c.idx))
    .map(c => ({
      idx: c.idx,
      name: c.name || `Camera ${c.idx}`,
      faces: FACE_KEYS.some(k => (c.detectors || []).includes(k)),
      allowed: ((c.detector_config || {}).unknown || {}).allowed_names || [],
    }));

  async function save(idx) {
    const d = await run('PUT', `/cameras/${idx}`,
                        { detector_config: { unknown: { allowed_names: draft } } },
                        'who is allowed');
    if (d) { setOpen(null); await load(); }
  }

  if (known.length === 0) {
    return (
      <Panel tone="warn" title="Nobody is enrolled yet">
        This is a list of people, so it needs people. Add your staff on the{' '}
        <strong>Staff</strong> step first — then come back and say which of them belongs
        at which camera.
      </Panel>
    );
  }

  return (
    <>
      {!cams && !err && (
        <div className="gs-panel"><div className="gs-scanning"><span className="gs-spin" /> Loading…</div></div>
      )}

      {cams && (
        <>
          <p className="gs-help">
            Leave a camera unrestricted and every enrolled person is let through it. Name
            people on a camera and it becomes a controlled door: anyone else — including
            staff enrolled for somewhere else — raises an unauthorised-access alert.
          </p>

          {rows.filter(r => r.faces).map(r => (
            <div key={r.idx}>
              <div className="gs-row">
                <span className="gs-row-icon">{r.allowed.length ? '🔐' : '🚪'}</span>
                <span className="gs-row-text">
                  <span className="gs-row-name">{r.name}</span>
                  <span className="gs-row-sub">
                    {r.allowed.length
                      ? `Only ${r.allowed.join(', ')}`
                      : 'No restriction — anyone enrolled is allowed through'}
                  </span>
                </span>
                {open === r.idx ? (
                  <button className="gs-btn small ghost" onClick={() => setOpen(null)}>Cancel</button>
                ) : (
                  <button className="gs-btn small"
                          onClick={() => { setOpen(r.idx); setDraft([...r.allowed]); setErr(null); }}>
                    {r.allowed.length ? 'Change' : 'Restrict'}
                  </button>
                )}
              </div>

              {open === r.idx && (
                <div className="gs-panel">
                  <div className="gs-panel-title">Who belongs at {r.name}?</div>
                  <div className="gs-chips">
                    {known.map(n => (
                      <button key={n} type="button"
                              className={`gs-chip${draft.includes(n) ? ' on' : ''}`}
                              onClick={() => setDraft(l => l.includes(n)
                                ? l.filter(x => x !== n) : [...l, n])}>
                        {n}
                      </button>
                    ))}
                  </div>
                  <p className="gs-help">
                    {draft.length === 0
                      ? 'Nobody picked — this camera will be left unrestricted, not closed to everyone.'
                      : `${draft.length} allowed. Everyone else raises an alert here, enrolled or not.`}
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button className="gs-btn primary" disabled={busy} onClick={() => save(r.idx)}>
                      {busy ? <span className="gs-spin" /> : 'Save'}
                    </button>
                    {r.allowed.length > 0 && (
                      <button className="gs-btn ghost" disabled={busy}
                              onClick={() => { setDraft([]); save(r.idx); }}>
                        Remove the restriction
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {rows.some(r => !r.faces) && (
            <Panel title="These cameras cannot check who somebody is">
              <strong>{rows.filter(r => !r.faces).map(r => r.name).join(', ')}</strong> —
              face recognition is off for {rows.filter(r => !r.faces).length === 1 ? 'it' : 'them'},
              so there is nobody to compare against a list. Switch it on for those cameras
              on the <strong>AI models</strong> step if they guard a door.
            </Panel>
          )}

          {/* Time-of-day restrictions exist in the config and are NOT enforced, so
              this panel does not offer them. Offering a control that saves and
              does nothing is worse than not having it. */}
          <p className="gs-help gs-help-after">
            This is a who, not a when — the restriction applies at every hour. Use the
            after-hours rule on the AI models step for time-based alerting.
          </p>

          {note && <p className="gs-help gs-help-after">{note}</p>}
          {err && <div className="gs-err" role="alert">{err}</div>}
        </>
      )}
    </>
  );
}
